import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

function parseDocsHash(hash: string) {
	if (!hash.startsWith("#docs"))
		return { isDocs: false, section: null as string | null };
	const after = hash.slice("#docs".length);
	if (after.startsWith("/")) {
		const section = decodeURIComponent(after.slice(1)) || null;
		return { isDocs: true, section };
	}
	return { isDocs: true, section: null as string | null };
}

export function DocsOverlay() {
	const [{ isDocs, section }, setRoute] = useState(() =>
		parseDocsHash(window.location.hash),
	);
	const [open, setOpen] = useState(isDocs);

	useEffect(() => {
		const onHash = () => {
			const next = parseDocsHash(window.location.hash);
			setRoute(next);
			setOpen(next.isDocs);
		};
		window.addEventListener("hashchange", onHash);
		// Initial sync in case hash changed before mount
		onHash();
		return () => window.removeEventListener("hashchange", onHash);
	}, []);

	// When opened directly at a deep link, scroll to that section
	useEffect(() => {
		if (!open) return;
		const id = section;
		const scrollTo = () => {
			if (!id) return;
			const el = document.getElementById(id);
			if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
		};
		const t = setTimeout(scrollTo, 50);
		return () => clearTimeout(t);
	}, [open, section]);

	const sections = useMemo(
		() => [
			{ id: "app", title: "App" },
			{ id: "events", title: "Events" },
			{ id: "naikira", title: "Naikira (NFT PFP Collection)" },
		],
		[],
	);

	return (
		<Sheet
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (next) {
					if (!window.location.hash.startsWith("#docs")) {
						history.pushState(null, "", "#docs");
					}
				} else {
					if (window.location.hash.startsWith("#docs")) {
						history.replaceState(null, "", "#");
					}
				}
			}}
		>
			<SheetContent side="right" className="w-full sm:max-w-2xl">
				<Card className="m-4 overflow-hidden">
					<CardContent className="p-0">
						<div className="grid grid-cols-1 md:grid-cols-[220px_1fr]">
							<nav className="border-r p-4 space-y-2 hidden md:block">
								{sections.map((s) => (
									<a
										key={s.id}
										href={`#docs/${s.id}`}
										className="text-sm hover:underline block"
									>
										{s.title}
									</a>
								))}
							</nav>
							<ScrollArea className="h-[70vh] md:h-[78vh] p-6">
								<div className="space-y-8">
									<section id="app">
										<h2 className="text-xl font-semibold">App</h2>
										<p className="mt-2 text-sm text-muted-foreground">
											ED3N is a gamified engagement platform that redefines how
											creators and fans connect, making active audience
											participation essential, not optional. Inspired by the
											always-on engagement seen with streamers, ED3N empowers
											Key Opinion Leaders (KOLs) on Instagram, TikTok, and other
											top platforms to deepen interaction, loyalty, and
											monetization. The app features AI companions modeled after
											each creator, allowing fans to engage directly with a
											digital persona that reflects the creator’s unique style
											and personality.
										</p>
										<p className="mt-2 text-sm text-muted-foreground">
											Fans can interact with these AI companions and unlock
											exclusive digital and physical rewards by spinning a
											gacha, which is accessed through creator-generated codes.
											Spins can be earned through engagement or purchased,
											creating a sustainable cycle of attention, participation,
											and revenue. ED3N delivers a clean, modern brand
											experience that puts meaningful, interactive engagement at
											the heart of the creator–fan relationship.
										</p>
									</section>

									<Separator />

									<section id="events">
										<h2 className="text-xl font-semibold">Events</h2>
										<p className="mt-2 text-sm text-muted-foreground">
											ED3N enables always-on engagement anchored by creator-led
											events and campaigns. Creators distribute unique codes
											across social channels and live activations; fans redeem
											them to spin gacha, unlock rewards, and climb
											leaderboards. Events can run on Instagram, TikTok, and
											beyond, while ED3N centralizes participation and
											fulfillment in one cohesive experience.
										</p>
									</section>

									<Separator />

									<section id="naikira">
										<h2 className="text-xl font-semibold">
											Naikira (NFT PFP Collection)
										</h2>
										<p className="mt-2 text-sm text-muted-foreground">
											Naikira is an NFT profile‑picture collection connected to
											the ED3N ecosystem. It integrates with the app to unlock
											themed rewards, special event access, and personalized AI
											companion experiences—deepening identity, loyalty, and
											community across the platform.
										</p>
									</section>
								</div>
							</ScrollArea>
						</div>
					</CardContent>
				</Card>
			</SheetContent>
		</Sheet>
	);
}

export default DocsOverlay;
